import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedNumber } from 'react-intl';
import {
  Button, Checkbox, Message, Modal, Segment, Table,
} from 'semantic-ui-react';

import {
  ACTIVATE_CONFIRM_LABEL,
  ACTIVATE_COVERAGE_NOTICE,
  ACTIVATE_DIALOG_HEADER,
  ACTIVATE_IRREVERSIBLE_NOTICE,
  BATCH_SELECTED_LABEL,
  DIALOG_CANCEL_LABEL,
  VALUE_PLACEHOLDER,
} from '../../constants/onboardingQueue';
import {
  coverageStartDate,
  dealerLockReason,
  hasAgreedPrice,
  isActivatable,
  priceProps,
} from '../../utilities/onboardingQueue';

const fieldValue = value => (value == null || value === '' ? VALUE_PLACEHOLDER : value);

const BatchActivateDialog = ({
  dealers, loading, errorMessage, onClose, onConfirm,
}) => {
  const [selected, setSelected] = useState(() => new Set(
    dealers.filter(isActivatable).map(dealer => dealer.flatRateOfferId)
  ));

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const toggle = flatRateOfferId => {
    if (loading) return;
    setSelected(current => {
      const next = new Set(current);
      if (next.has(flatRateOfferId)) next.delete(flatRateOfferId);
      else next.add(flatRateOfferId);
      return next;
    });
  };

  const handleConfirm = () => {
    if (loading || selected.size === 0) return;
    onConfirm(dealers
      .filter(dealer => selected.has(dealer.flatRateOfferId))
      .map(dealer => dealer.flatRateOfferId));
  };

  return (
    <Modal
      open
      size='small'
      onClose={handleClose}
      closeOnEscape={!loading}
      closeOnDimmerClick={false}
      data-testid='queue-batch-activate-dialog'
    >
      <Modal.Header>{ACTIVATE_DIALOG_HEADER}</Modal.Header>
      <Modal.Content>
        <Segment data-testid='queue-batch-activate-summary'>
          <div>
            <b>Product:</b>
            {' '}
            <span data-testid='queue-batch-activate-product'>{fieldValue(dealers[0].productType)}</span>
          </div>
          <div>
            <b>{ACTIVATE_COVERAGE_NOTICE}</b>
            {' '}
            <span data-testid='queue-batch-activate-coverage-date'>{coverageStartDate()}</span>
          </div>
        </Segment>

        <Table compact celled data-testid='queue-batch-activate-table'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell collapsing />
              <Table.HeaderCell>Customer</Table.HeaderCell>
              <Table.HeaderCell>Customer ID</Table.HeaderCell>
              <Table.HeaderCell>Price</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {dealers.map(dealer => {
              const lockReason = dealerLockReason(dealer);
              return (
                <Table.Row
                  key={dealer.flatRateOfferId}
                  disabled={Boolean(lockReason)}
                  data-testid='queue-batch-activate-row'
                >
                  <Table.Cell collapsing>
                    <Checkbox
                      checked={selected.has(dealer.flatRateOfferId)}
                      disabled={loading || Boolean(lockReason)}
                      aria-label={fieldValue(dealer.dealerName)}
                      data-testid='queue-batch-activate-checkbox'
                      onChange={() => toggle(dealer.flatRateOfferId)}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <div data-testid='queue-batch-activate-customer'>{fieldValue(dealer.dealerName)}</div>
                    {lockReason && (
                      <small data-testid='queue-batch-activate-lock-reason'>{lockReason}</small>
                    )}
                  </Table.Cell>
                  <Table.Cell data-testid='queue-batch-activate-customer-id'>
                    {fieldValue(dealer.customerId)}
                  </Table.Cell>
                  <Table.Cell data-testid='queue-batch-activate-price'>
                    {hasAgreedPrice(dealer.subscriptionPrice)
                      ? React.createElement(FormattedNumber, priceProps(dealer.subscriptionPrice))
                      : VALUE_PLACEHOLDER}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>

        <p data-testid='queue-batch-activate-count'>
          {BATCH_SELECTED_LABEL(selected.size, dealers.length)}
        </p>

        <Message warning data-testid='queue-batch-activate-irreversible'>
          <p>{ACTIVATE_IRREVERSIBLE_NOTICE}</p>
        </Message>

        {errorMessage && (
          <Message negative data-testid='queue-batch-activate-error'>
            <p>{errorMessage}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={loading} data-testid='queue-batch-activate-cancel'>
          {DIALOG_CANCEL_LABEL}
        </Button>
        <Button
          primary
          loading={loading}
          disabled={loading || selected.size === 0}
          data-testid='queue-batch-activate-confirm'
          onClick={handleConfirm}
        >
          {ACTIVATE_CONFIRM_LABEL}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

BatchActivateDialog.propTypes = {
  dealers: PropTypes.arrayOf(PropTypes.shape({
    flatRateOfferId: PropTypes.string,
    dealerName: PropTypes.string,
    customerId: PropTypes.string,
    productType: PropTypes.string,
    subscriptionPrice: PropTypes.number,
    status: PropTypes.string,
  })).isRequired,
  loading: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

BatchActivateDialog.defaultProps = {
  loading: false,
  errorMessage: null,
};

export default BatchActivateDialog;
